// Diversification Optimizer PRO -- clickable ticker.
//
// Renders a symbol as an anchor that opens its asset deep-dive page in a NEW
// browser tab (target="_blank"). Using a real <a href> (rather than an onClick
// + window.open) means middle-click / cmd-click work natively and no popup
// blocker ever intervenes. The href is resolved upstream by useSymbolRoutes;
// if a symbol has not resolved yet we fall back to the equity deep-dive so the
// link is always valid.

import React from "react";
import { ExternalLink } from "lucide-react";

import { buildSymbolHref } from "../useSymbolRoutes";

interface SymbolLinkProps {
  symbol: string;
  /** Resolved map (normalised symbol -> href) from useSymbolRoutes. */
  routes?: Record<string, string>;
  className?: string;
}

const BASE_CLASS =
  "group inline-flex items-center gap-1 font-mono font-bold text-indigo-600 " +
  "hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 " +
  "hover:underline underline-offset-2 transition-colors";

export const SymbolLink: React.FC<SymbolLinkProps> = ({ symbol, routes, className }) => {
  const key = symbol.trim().toUpperCase();
  const href = routes?.[key] ?? buildSymbolHref(symbol, null);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={`Open ${symbol} deep-dive in a new tab`}
      className={className ?? BASE_CLASS}
    >
      {symbol}
      <ExternalLink
        className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity"
        aria-hidden="true"
      />
    </a>
  );
};
